
const libPictView = require('pict-view');

/**
 * Data Lake dataset viewer — #/Datalake/Dataset/:GUID.
 *
 * Shows one lake dataset: a type-aware header (imported clone vs computed projection), a metadata /
 * provenance panel, the column schema, a paginated table of the actual rows (columns derived at runtime
 * from the dataset's JSON-Schema), and lineage. State + behaviour live in DatalakeDatasetProvider; this
 * view derives + repaints declaratively. The management action bar (refresh / schedule / re-import) is
 * added by the management phase.
 */
class DatalakeDataset extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;
	}

	get provider()
	{
		return this.pict.providers.DatalakeDatasetProvider;
	}

	deriveRenderState()
	{
		if (this.provider)
		{
			this.provider.deriveRenderState();
		}
	}

	onBeforeRender(pRenderable)
	{
		this.deriveRenderState();
		return super.onBeforeRender(pRenderable);
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		this.pict.CSSMap.injectCSS();
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}
}

module.exports = DatalakeDataset;

module.exports.default_configuration =
{
	ViewIdentifier: 'DatalakeDataset',
	DefaultRenderable: 'DatalakeDatasetWrap',
	DefaultDestinationAddress: '#PlatformConfiguration-Application-Container',
	AutoRender: false,

	CSS: /*css*/`
		.dld-wrap { padding: 1.25rem 1.5rem 2rem; }
		.dld-back { margin-bottom: 0.5rem; }
		.dld-head { display: flex; align-items: flex-start; gap: 0.8rem; margin-bottom: 1rem; }
		.dld-title { font-size: 1.4rem; font-weight: 700; line-height: 1.1; }
		.dld-sub { color: var(--bulma-text-weak, #7a7a7a); font-size: 0.9rem; }
		.dld-cols2 { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 1rem; margin-bottom: 1rem; }
		@media (max-width: 980px) { .dld-cols2 { grid-template-columns: minmax(0,1fr); } }
		.dld-panel { background: var(--bulma-scheme-main-bis, #f8fafc); border: 1px solid var(--bulma-border, #e6e9ef); border-radius: 10px; padding: 0.85rem 1rem; }
		.dld-panel h3 { font-size: 0.78rem; text-transform: uppercase; letter-spacing: 0.04em; color: var(--bulma-text-weak, #7a7a7a); font-weight: 700; margin-bottom: 0.5rem; }
		.dld-meta-row { display: flex; gap: 0.6rem; padding: 0.18rem 0; font-size: 0.86rem; }
		.dld-meta-label { flex: 0 0 8rem; color: var(--bulma-text-weak, #7a7a7a); }
		.dld-meta-val { min-width: 0; word-break: break-word; }
		.dld-meta-val code, .dld-schema code { color: var(--bulma-text-strong, #363636); }
		.dld-schema { }
		.dld-data-head { display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin: 0.5rem 0; }
		.dld-table-scroll { overflow-x: auto; border: 1px solid var(--bulma-border, #e6e9ef); border-radius: 8px; }
		.dld-table { font-size: 0.82rem; white-space: nowrap; margin-bottom: 0; }
		.dld-table th { position: sticky; top: 0; background: var(--bulma-scheme-main-bis, #f2f5f9); }
		.dld-table td { max-width: 22rem; overflow: hidden; text-overflow: ellipsis; }
		.dld-empty { padding: 1.5rem 1rem; }
		.dld-spinner { display: inline-block; width: 1.4rem; height: 1.4rem; border: 3px solid var(--bulma-border, #dbdbdb); border-top-color: var(--bulma-link, #156dd1); border-radius: 50%; animation: dld-spin 0.8s linear infinite; }
		@keyframes dld-spin { to { transform: rotate(360deg); } }
		.dld-lineage code { color: var(--bulma-text-weak, #8a93a3); }
		.dld-manage-row { display: flex; align-items: center; margin-bottom: 0.75rem; }
		.dld-sched { display: flex; align-items: center; flex-wrap: wrap; gap: 0.75rem; padding-top: 0.5rem; border-top: 1px dashed var(--bulma-border, #e6e9ef); }
		.dld-sched-modes { display: flex; gap: 0.9rem; }
		.dld-sched .field { margin-bottom: 0; }
		.dld-sched .input { max-width: 11rem; }
	`,

	Templates:
	[
		{
			Hash: 'Datalake-DS-Wrap',
			Template: /*html*/`
				<div class="dld-wrap">
					<div class="dld-back"><a class="button is-small is-light" onclick="_Pict.providers.DatalakeDatasetProvider.backToCatalog()"><span class="icon is-small"><i class="fas fa-arrow-left"></i></span><span>Data Lake</span></a></div>

					{~TS:Datalake-DS-Error:AppData.DatalakeDataset.ErrorSlot~}
					{~TS:Datalake-DS-RawAction:AppData.DatalakeDataset.RawActionSlot~}
					{~TS:Datalake-DS-Loading:AppData.DatalakeDataset.LoadingSlot~}

					<div class="dld-head">
						<div style="flex:1 1 auto; min-width:0;">
							<div class="dld-title">{~D:AppData.DatalakeDataset.HeaderName~}</div>
							<div class="dld-sub">{~D:AppData.DatalakeDataset.HeaderSub~}</div>
						</div>
						<span class="tag {~D:AppData.DatalakeDataset.ChipClass~}"><span class="icon is-small"><i class="fas {~D:AppData.DatalakeDataset.ChipIcon~}"></i></span><span>{~D:AppData.DatalakeDataset.ChipLabel~}</span></span>
					</div>

					<div class="dld-cols2">
						<div class="dld-panel">
							<h3>Dataset</h3>
							{~TS:Datalake-DS-MetaRow:AppData.DatalakeDataset.MetaRows~}
						</div>
						<div class="dld-panel">
							<h3>Schema ({~D:AppData.DatalakeDataset.SchemaColumnCount~} columns)</h3>
							<div class="dld-schema">
								<table class="table is-narrow is-fullwidth">
									<tbody>{~TS:Datalake-DS-SchemaRow:AppData.DatalakeDataset.SchemaRows~}</tbody>
								</table>
							</div>
						</div>
					</div>

					{~TS:Datalake-DS-Manage:AppData.DatalakeDataset.ManageSlot~}

					{~TS:Datalake-DS-Lineage:AppData.DatalakeDataset.LineageSlot~}

					<div class="dld-panel">
						<div class="dld-data-head">
							<h3 style="margin:0;">Data</h3>
							<div class="field has-addons" style="margin:0;">
								<span class="dld-sub" style="margin-right:0.6rem;">{~D:AppData.DatalakeDataset.PageLabel~}</span>
								<p class="control"><button class="button is-small" {~D:AppData.DatalakeDataset.PrevDisabledAttr~} onclick="_Pict.providers.DatalakeDatasetProvider.prevPage()"><span class="icon is-small"><i class="fas fa-chevron-left"></i></span></button></p>
								<p class="control"><button class="button is-small" {~D:AppData.DatalakeDataset.NextDisabledAttr~} onclick="_Pict.providers.DatalakeDatasetProvider.nextPage()"><span class="icon is-small"><i class="fas fa-chevron-right"></i></span></button></p>
							</div>
						</div>
						<p class="dld-sub mb-2">{~D:AppData.DatalakeDataset.ColumnsTruncatedNote~}</p>
						<div class="dld-table-scroll">
							<table class="table is-narrow is-hoverable is-fullwidth dld-table">
								<thead><tr>{~TS:Datalake-DS-ColHead:AppData.DatalakeDataset.Columns~}</tr></thead>
								<tbody>{~TS:Datalake-DS-Row:AppData.DatalakeDataset.Rows~}</tbody>
							</table>
						</div>
						{~TS:Datalake-DS-RowsLoading:AppData.DatalakeDataset.RowsLoadingSlot~}
						{~TS:Datalake-DS-RowsEmpty:AppData.DatalakeDataset.RowsEmptySlot~}
					</div>
				</div>`
		},
		{ Hash: 'Datalake-DS-MetaRow', Template: /*html*/`<div class="dld-meta-row"><span class="dld-meta-label">{~D:Record.Label~}</span><span class="dld-meta-val"><code>{~D:Record.Value~}</code></span></div>` },
		{ Hash: 'Datalake-DS-SchemaRow', Template: /*html*/`<tr><td><code>{~D:Record.Column~}</code></td><td class="has-text-grey">{~D:Record.Type~}</td><td class="has-text-grey is-size-7">{~D:Record.Size~}</td></tr>` },
		{ Hash: 'Datalake-DS-ColHead', Template: /*html*/`<th>{~D:Record.Label~}</th>` },
		{ Hash: 'Datalake-DS-Row', Template: /*html*/`<tr>{~TS:Datalake-DS-Cell:Record.Cells~}</tr>` },
		{ Hash: 'Datalake-DS-Cell', Template: /*html*/`<td title="{~D:Record.Value~}">{~D:Record.Value~}</td>` },
		{
			Hash: 'Datalake-DS-Lineage',
			Template: /*html*/`
				<div class="dld-panel dld-lineage mb-4">
					<h3>Lineage</h3>
					<div class="columns">
						<div class="column"><strong class="is-size-7">Upstream (feeds this)</strong>{~TS:Datalake-DS-LineageItem:AppData.DatalakeDataset.LineageUpstream~}</div>
						<div class="column"><strong class="is-size-7">Downstream (reads this)</strong>{~TS:Datalake-DS-LineageItem:AppData.DatalakeDataset.LineageDownstream~}</div>
					</div>
				</div>`
		},
		{ Hash: 'Datalake-DS-LineageItem', Template: /*html*/`<div class="is-size-7"><code>{~D:Record.Address~}</code></div>` },
		{
			Hash: 'Datalake-DS-Manage',
			Template: /*html*/`
				<div class="dld-panel mb-4">
					<h3>Manage</h3>
					{~TS:Datalake-DS-ActionResult:AppData.DatalakeDataset.ActionResultSlot~}
					{~TS:Datalake-DS-ManageComputed:AppData.DatalakeDataset.ComputedManageSlot~}
					{~TS:Datalake-DS-ManageImported:AppData.DatalakeDataset.ImportedManageSlot~}
				</div>`
		},
		{
			Hash: 'Datalake-DS-ManageComputed',
			Template: /*html*/`
				<div class="dld-manage">
					<div class="dld-manage-row">
						<button class="button is-link is-small" {~D:AppData.DatalakeDataset.RefreshDisabledAttr~} onclick="_Pict.providers.DatalakeDatasetProvider.refreshNow()"><span class="icon is-small"><i class="fas fa-rotate"></i></span><span>Refresh now</span></button>
						{~TS:Datalake-DS-ActionBusy:AppData.DatalakeDataset.ActionBusySlot~}
					</div>
					<div class="dld-sched">
						<label class="checkbox"><input type="checkbox" {~D:AppData.DatalakeDataset.ScheduleEnabledAttr~} onchange="_Pict.providers.DatalakeDatasetProvider.setScheduleEnabledLocal(this.checked)" /> Scheduled automatic refresh</label>
						<div class="dld-sched-modes">
							<label class="radio"><input type="radio" name="dld-sched-mode" {~D:AppData.DatalakeDataset.ScheduleModeIntervalChecked~} onchange="_Pict.providers.DatalakeDatasetProvider.setScheduleMode('interval')" /> Every N minutes</label>
							<label class="radio"><input type="radio" name="dld-sched-mode" {~D:AppData.DatalakeDataset.ScheduleModeCronChecked~} onchange="_Pict.providers.DatalakeDatasetProvider.setScheduleMode('cron')" /> Cron (UTC)</label>
						</div>
						<div class="field {~D:AppData.DatalakeDataset.ScheduleIntervalClass~}"><div class="control"><input class="input is-small" type="number" min="1" placeholder="minutes" value="{~D:AppData.DatalakeDataset.Schedule.IntervalMinutes~}" oninput="_Pict.providers.DatalakeDatasetProvider.setScheduleField('interval', this.value)" /></div></div>
						<div class="field {~D:AppData.DatalakeDataset.ScheduleCronClass~}"><div class="control"><input class="input is-small" type="text" placeholder="0 9 * * *" value="{~D:AppData.DatalakeDataset.Schedule.Cron~}" oninput="_Pict.providers.DatalakeDatasetProvider.setScheduleField('cron', this.value)" /></div></div>
						<button class="button is-small" {~D:AppData.DatalakeDataset.RefreshDisabledAttr~} onclick="_Pict.providers.DatalakeDatasetProvider.saveSchedule()"><span class="icon is-small"><i class="fas fa-floppy-disk"></i></span><span>Save schedule</span></button>
					</div>
				</div>`
		},
		{
			Hash: 'Datalake-DS-ManageImported',
			Template: /*html*/`
				<div class="dld-manage">
					<p class="dld-sub mb-2">This is a read-only imported copy of a source system. Re-importing re-pulls the source into the lake (the source is durable, so this is safe to repeat).</p>
					<div class="dld-manage-row">
						<button class="button is-link is-small" {~D:AppData.DatalakeDataset.RefreshDisabledAttr~} onclick="_Pict.providers.DatalakeDatasetProvider.reImport()"><span class="icon is-small"><i class="fas fa-cloud-arrow-down"></i></span><span>Re-import from source</span></button>
						{~TS:Datalake-DS-ActionBusy:AppData.DatalakeDataset.ActionBusySlot~}
					</div>
				</div>`
		},
		{ Hash: 'Datalake-DS-ActionResult', Template: /*html*/`<div class="notification {~D:Record.Class~} is-soft py-2 px-3 mb-2"><span class="icon"><i class="fas {~D:Record.Icon~}"></i></span> <span>{~D:Record.Message~}</span></div>` },
		{ Hash: 'Datalake-DS-ActionBusy', Template: /*html*/`<span class="dld-spinner" style="width:1.1rem; height:1.1rem; margin-left:0.6rem;"></span>` },
		{ Hash: 'Datalake-DS-Error', Template: /*html*/`<div class="notification is-danger is-soft py-2 px-3 mb-3"><span class="icon"><i class="fas fa-triangle-exclamation"></i></span> <span>{~D:Record.Message~}</span></div>` },
		{ Hash: 'Datalake-DS-RawAction', Template: /*html*/`<div class="mb-4"><button class="button is-link" onclick="_Pict.providers.DatalakeDatasetProvider.projectionizeThis()"><span class="icon"><i class="fas fa-wand-magic-sparkles"></i></span><span>Create a projection from this table</span></button></div>` },
		{ Hash: 'Datalake-DS-Loading', Template: /*html*/`<div class="dld-empty has-text-grey has-text-centered"><span class="dld-spinner"></span><p class="mt-2">Loading dataset…</p></div>` },
		{ Hash: 'Datalake-DS-RowsLoading', Template: /*html*/`<div class="dld-empty has-text-grey has-text-centered"><span class="dld-spinner"></span></div>` },
		{ Hash: 'Datalake-DS-RowsEmpty', Template: /*html*/`<div class="dld-empty has-text-grey has-text-centered"><p>No rows in this page.</p></div>` }
	],

	Renderables:
	[
		{
			RenderableHash: 'DatalakeDatasetWrap',
			TemplateHash: 'Datalake-DS-Wrap',
			ContentDestinationAddress: '#PlatformConfiguration-Application-Container'
		}
	]
};
